import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame, type ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import type { BodyId } from "@solar/ephemeris";
import { resolveQuality, useScene } from "../store";
import { loadTexture, useRealMaps } from "./realTextures";
import { bodyLive, bodyQuaternion } from "./bodyState";
import { frame, sunRelUniform } from "./frame";
import { bodyTextures, ringTexture, sunTexture } from "./materials";

const RING_INNER = 1.24, RING_OUTER = 2.33; // 与 2k_saturn_ring_alpha.png 的结构对齐（卡西尼缝约在 u≈0.69）
/** 视觉自转：时间流速 ≤ 此值（天/秒）用真实相位；更快则限速，避免频闪。 */
const TRUE_SPIN_MAX_SPEED = 1;
const VISUAL_SPIN_DEG_PER_SEC = 90;

/** 球体网格精度随画质分档。 */
function useSegments(): [number, number] {
  const q = resolveQuality(useScene((s) => s.quality));
  return q === "low" ? [32, 24] : q === "medium" ? [48, 36] : [64, 48];
}

function useSelectHandlers(id: BodyId) {
  const select = useScene((s) => s.select);
  return {
    onClick: (e: ThreeEvent<MouseEvent>) => {
      if (e.delta > 5) return; // 拖动不算点击
      e.stopPropagation();
      select(id);
    },
    onPointerOver: (e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
    onPointerOut: () => { document.body.style.cursor = ""; },
  };
}

function useLive(id: BodyId) {
  useEffect(() => {
    bodyLive.set(id, { world: new THREE.Vector3(), north: new THREE.Vector3(0, 1, 0), radius: 1, visible: true });
    return () => { bodyLive.delete(id); };
  }, [id]);
}

/** 土星环与球体互相投影：解析求交（不用阴影贴图——真实比例下阴影贴图的分辨率远不够）。 */
const saturnU = {
  uSunV: { value: new THREE.Vector3() }, uCenterV: { value: new THREE.Vector3() }, uAxisV: { value: new THREE.Vector3(0, 1, 0) }, uRad: { value: 1 },
};
const SHADOW_DECL = `uniform vec3 uSunV; uniform vec3 uCenterV; uniform vec3 uAxisV; uniform float uRad;`;
const GLOBE_SHADOW = `
  {
    vec3 q = (-vViewPosition - uCenterV) / uRad;
    vec3 L = normalize(uSunV + vViewPosition);
    float den = dot(L, uAxisV);
    float f = 1.0;
    if (abs(den) > 1e-3) {
      float t = -dot(q, uAxisV) / den;
      if (t > 0.0) { float rho = length(q + t * L); if (rho > ${RING_INNER.toFixed(2)} && rho < ${RING_OUTER.toFixed(2)}) f = 0.22; }
    }
    reflectedLight.directDiffuse *= f; reflectedLight.directSpecular *= f;
  }`;
const RING_SHADOW = `
  {
    vec3 q = (-vViewPosition - uCenterV) / uRad;
    vec3 L = normalize(uSunV + vViewPosition);
    float b = dot(q, L), c = dot(q, q) - 1.0, disc = b * b - c;
    float f = 1.0;
    if (disc > 0.0 && (-b + sqrt(disc)) > 0.0) f = 0.1;
    reflectedLight.directDiffuse *= f; reflectedLight.directSpecular *= f;
  }`;
function patchShadow(m: THREE.MeshStandardMaterial, body: string) {
  m.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, saturnU);
    sh.fragmentShader = sh.fragmentShader.replace("#include <common>", `#include <common>\n${SHADOW_DECL}`).replace("#include <lights_fragment_end>", `#include <lights_fragment_end>\n${body}`);
  };
}

export function Sun() {
  const hidden = useScene((s) => s.hidden.has("sun"));
  const procedural = useMemo(() => sunTexture(), []);
  const [real, setReal] = useState<THREE.Texture | null>(null);
  useEffect(() => { let on = true; loadTexture("sun").then((t) => on && setReal(t)); return () => { on = false; }; }, []);
  const tex = real ?? procedural;
  const [ws, hs] = useSegments();
  const handlers = useSelectHandlers("sun");
  const g = useRef<THREE.Group>(null);
  useLive("sun");
  useFrame(() => {
    const live = bodyLive.get("sun");
    if (!g.current || !live) return;
    g.current.position.copy(live.world).sub(frame.origin);
    g.current.scale.setScalar(live.radius);
  });
  if (hidden) { const l = bodyLive.get("sun"); if (l) l.visible = false; return null; }
  return (
    <group ref={g}>
      <mesh {...handlers}>
        <sphereGeometry args={[1, ws, hs]} />
        <meshBasicMaterial map={tex} />
      </mesh>
    </group>
  );
}

export function Body({ id }: { id: BodyId }) {
  const hidden = useScene((s) => s.hidden.has(id) || (id === "moon" && s.hidden.has("earth")));
  const showAxis = useScene((s) => s.layers.axis);
  const handlers = useSelectHandlers(id);
  const procTex = useMemo(() => bodyTextures(id), [id]);
  const realMaps = useRealMaps(id);
  const [ws, hs] = useSegments();
  // 真实贴图加载完成后无缝替换；加载失败则一直用程序生成的贴图（需求 A11）
  const textures = useMemo(() => ({
    map: (realMaps?.map ?? procTex.map) as THREE.Texture,
    roughness: (realMaps?.rough ?? procTex.roughness) as THREE.Texture | undefined,
    emissive: (realMaps?.night ?? procTex.emissive) as THREE.Texture | undefined,
    clouds: (realMaps?.clouds ?? procTex.clouds) as THREE.Texture | undefined,
    cloudsAreAlphaMap: !!realMaps?.clouds,
  }), [procTex, realMaps]);
  const procRing = useMemo(() => (id === "saturn" ? ringTexture() : null), [id]);
  const [realRing, setRealRing] = useState<THREE.Texture | null>(null);
  useEffect(() => { if (id !== "saturn") return; let on = true; loadTexture("saturn-ring").then((t) => on && setRealRing(t)); return () => { on = false; }; }, [id]);
  const ring = realRing ?? procRing;
  const pos = useRef<THREE.Group>(null);
  const orient = useRef<THREE.Group>(null);
  const spin = useRef({ visual: 0 });
  useLive(id);

  // 地球夜面灯光：只在背光面显示（自发光贴图 × 昼夜遮罩）；太阳位置用相对原点的 uniform
  const earthMat = useMemo(() => {
    if (id !== "earth") return null;
    const m = new THREE.MeshStandardMaterial({
      map: textures.map, roughnessMap: textures.roughness, roughness: 1, metalness: 0,
      emissiveMap: textures.emissive, emissive: new THREE.Color(1, 1, 1), emissiveIntensity: 1.1,
    });
    m.onBeforeCompile = (sh) => {
      sh.uniforms.uSunRel = sunRelUniform;
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", "#include <common>\nuniform vec3 uSunRel;")
        .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
         vec3 sunV = (viewMatrix * vec4(uSunRel, 1.0)).xyz;
         vec3 toSun = normalize(sunV + vViewPosition);
         float dayK = smoothstep(-0.02, 0.14, dot(normalize(normal), toSun));
         totalEmissiveRadiance *= (1.0 - dayK);`);
    };
    return m;
  }, [id, textures]);
  const saturnGlobe = useMemo(() => {
    if (id !== "saturn") return null;
    const m = new THREE.MeshStandardMaterial({ map: textures.map, roughness: 0.92, metalness: 0 });
    patchShadow(m, GLOBE_SHADOW);
    return m;
  }, [id, textures]);

  useFrame(({ camera }, dt) => {
    const g = pos.current, o = orient.current, live = bodyLive.get(id);
    if (!g || !o || !live) return;
    g.position.copy(live.world).sub(frame.origin);
    g.scale.setScalar(live.radius);
    const s = useScene.getState();
    const date = new Date(s.timeMs);
    const base = bodyQuaternion(id, date, 0);
    // 自转：低速用真实相位；高速限速（保持连续）
    let offset = 0;
    if (Math.abs(s.speed) > TRUE_SPIN_MAX_SPEED && s.playing) {
      const sign = id === "venus" || id === "uranus" || id === "pluto" ? -1 : 1;
      spin.current.visual += sign * VISUAL_SPIN_DEG_PER_SEC * dt;
      offset = spin.current.visual - base.trueSpinDeg;
    } else spin.current.visual = base.trueSpinDeg;
    o.quaternion.copy(offset ? bodyQuaternion(id, date, offset).q : base.q);
    live.north.copy(base.north); live.visible = true;
    if (id === "saturn") {
      camera.updateMatrixWorld();
      const inv = camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
      saturnU.uSunV.value.copy(frame.origin).negate().applyMatrix4(inv);       // 太阳在原点（绝对 0）→ 相对 = −origin
      saturnU.uCenterV.value.copy(g.position).applyMatrix4(inv);
      saturnU.uAxisV.value.copy(base.north).transformDirection(inv);
      saturnU.uRad.value = live.radius;
    }
  });

  if (hidden) { const l = bodyLive.get(id); if (l) l.visible = false; return null; }
  const isEarth = id === "earth", isSaturn = id === "saturn";
  return (
    <group ref={pos}>
      <group ref={orient}>
        <mesh {...handlers}>
          <sphereGeometry args={[1, ws, hs]} />
          {isEarth && earthMat ? <primitive object={earthMat} attach="material" /> : isSaturn && saturnGlobe ? <primitive object={saturnGlobe} attach="material" /> : (
            <meshStandardMaterial map={textures.map} roughness={0.92} metalness={0} />
          )}
        </mesh>
        {isEarth && textures.clouds && (
          <mesh scale={1.012} raycast={() => null}>
            <sphereGeometry args={[1, ws, hs]} />
            {textures.cloudsAreAlphaMap
              ? <meshStandardMaterial color="#ffffff" alphaMap={textures.clouds} transparent depthWrite={false} roughness={1} />
              : <meshStandardMaterial map={textures.clouds} transparent depthWrite={false} roughness={1} />}
          </mesh>
        )}
        {isSaturn && ring && <SaturnRing texture={ring} />}
        {showAxis && <AxisLine ringed={isSaturn} />}
      </group>
    </group>
  );
}

function SaturnRing({ texture }: { texture: THREE.Texture }) {
  const geo = useMemo(() => {
    const g = new THREE.RingGeometry(RING_INNER, RING_OUTER, 128, 1);
    const p = g.attributes.position!, uv = g.attributes.uv!;
    for (let i = 0; i < p.count; i++) uv.setXY(i, (Math.hypot(p.getX(i), p.getY(i)) - RING_INNER) / (RING_OUTER - RING_INNER), 0.5); // 径向取样
    return g;
  }, []);
  const mat = useMemo(() => {
    const m = new THREE.MeshStandardMaterial({ map: texture, transparent: true, side: THREE.DoubleSide, roughness: 0.9, metalness: 0, depthWrite: false });
    patchShadow(m, RING_SHADOW);
    return m;
  }, [texture]);
  return <mesh geometry={geo} material={mat} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null} />;
}

function AxisLine({ ringed }: { ringed: boolean }) {
  const L = ringed ? 2.7 : 1.9;
  const line = useMemo(() => {
    const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, -L, 0), new THREE.Vector3(0, L, 0)]);
    const l = new THREE.Line(geo, new THREE.LineDashedMaterial({ color: 0xffdc8c, dashSize: 0.22, gapSize: 0.14, transparent: true, opacity: 0.8 }));
    l.computeLineDistances();
    l.raycast = () => null;
    return l;
  }, [L]);
  return <primitive object={line} />;
}
